import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, useState } from 'react';

import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/util';
import { MOUSE_EVENT_BUTTONS } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/const';
import { createTemplatePlaceholder } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/util';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './TemplatePlaceholderContextMenu';
import { PLACEHOLDER_BLUE } from './colours';

export type TemplateCapableTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string | TemplatePlaceholder;
  onChange: (
    newValue: string | TemplatePlaceholder,
    reason: 'normal' | 'setIsTemplatePlaceholder' | 'unsetIsTemplatePlaceholder'
  ) => void;
  isError?: boolean;
  isOnTemplatePage?: boolean;
  templateBuilderDisplayName?: string;

  /** This is put in the actual placeholder prop of the underlying htmlInput
   * when the value is a placeholder. Defaults to ''Placeholder field'*/
  templateBuilderPlaceholderFieldLabel?: string;
};

export default function TemplateCapableTextField({
  value,
  onChange,
  templateBuilderDisplayName = '',
  isOnTemplatePage = false,
  disabled = false,
  sx = {},
  slotProps = {},
  templateBuilderPlaceholderFieldLabel = DEFAULT_PLACEHOLDER_FIELD_LABEL,
  ...props
}: TemplateCapableTextFieldProps) {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const valueIsPlaceholder = isTemplatePlaceholder(value);

  const conditionalPlaceholderProps: Partial<TextFieldProps> = valueIsPlaceholder
    ? {
        // The following gives it the placeholder styling
        color: 'info',
        sx: {
          ...sx,
          '& .MuiFilledInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          caretColor: 'transparent',
        },
        focused: true,
        slotProps: {
          ...slotProps,
          htmlInput: {
            placeholder: templateBuilderPlaceholderFieldLabel,
            ...slotProps?.htmlInput,
            readOnly: true,
            onMouseDown: (e: { button: number; preventDefault: () => void }) => {
              // Only prevent default if it's a left click (0)
              // This keeps Right Click (2) working for the context menu
              if (e.button === 0) {
                e.preventDefault();
              }
            },
          },
        },
      }
    : {
        slotProps,
        sx,
      };

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      onChange(createTemplatePlaceholder(templateBuilderDisplayName), 'setIsTemplatePlaceholder');
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      onChange('', 'unsetIsTemplatePlaceholder');
    }
  };

  const handleNormalOnChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onChange(event.target.value, 'normal');
  };

  if (isOnTemplatePage) {
    return (
      <>
        <TextField
          value={valueIsPlaceholder ? '' : value}
          onChange={handleNormalOnChange}
          onMouseDownCapture={(evt: React.MouseEvent) => {
            if (evt.button === MOUSE_EVENT_BUTTONS.right) {
              evt.preventDefault();
              evt.stopPropagation();
            }
          }}
          onContextMenuCapture={(evt: React.MouseEvent) => {
            evt.preventDefault();
            evt.stopPropagation();

            if (!disabled) {
              handleOpenPlaceholderContextMenu(evt, placeholderMenuState, setPlaceholderMenuState);
            }
          }}
          {...conditionalPlaceholderProps}
          disabled={disabled}
          {...props}
        />
        <TemplatePlaceholderContextMenu
          menuState={placeholderMenuState}
          placeholderActive={valueIsPlaceholder}
          onClose={handleCloseTemplatePlaceholderContextMenu}
        />
      </>
    );
  }

  if (valueIsPlaceholder) {
    throw new Error(
      'TemplateCapableTextField: value is a placeholder but isOnTemplatePage is false'
    );
  }

  return (
    <TextField
      value={value}
      onChange={handleNormalOnChange}
      sx={sx}
      slotProps={slotProps}
      {...props}
    />
  );
}
